import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import { Sparkles, ArrowLeftRight, MessageCircle, Link2, Calendar, Users, HandCoins, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { markMatchesSeen } from "@/components/mobile/NotificationBell";
import PullToRefresh from "@/components/mobile/PullToRefresh";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";
const imgOf = (room) => (room?.photos && room.photos[0]) ? fileUrl(room.photos[0]) : FALLBACK_IMG;

const reveal = (i = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
});

export default function Matches() {
    const [tab, setTab] = useState("direct");
    const [matches, setMatches] = useState([]);
    const [chains, setChains] = useState([]);
    const [buddies, setBuddies] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const [m, c, b] = await Promise.all([
                api.get("/matches"),
                api.get("/matches/chain"),
                api.get("/matches/buddies").catch(() => ({ data: [] })),
            ]);
            setMatches(m.data); setChains(c.data); setBuddies(b.data || []);
            markMatchesSeen((m.data?.length || 0) + (c.data?.length || 0) + ((b.data || []).length));
        } catch { /* ignore */ } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const total = matches.length + chains.length + buddies.length;

    return (
        <PullToRefresh onRefresh={load}>
        <div data-testid="matches-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 sm:py-10">
            {/* ── Hero ─────────────────────────────────────────────── */}
            <motion.section
                {...reveal(0)}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
            >
                <div className="absolute -right-14 -top-14 w-64 h-64 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative">
                    <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70 inline-flex items-center gap-2">
                        <ArrowLeftRight className="w-3.5 h-3.5" /> Matches
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">
                        Your swap suggestions
                    </h1>
                    <p className="text-white/80 mt-2 text-sm sm:text-base max-w-lg">
                        Date overlap and matching cities. We do the searching. You pick the vibe.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur rounded-full px-4 py-2 text-sm font-semibold">
                        <Sparkles className="w-4 h-4" /> {total} total {total === 1 ? "match" : "matches"}
                    </div>
                </div>
            </motion.section>

            {/* ── Tab pills ─────────────────────────────────────────── */}
            <motion.div {...reveal(0.05)} className="mt-6 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-hide">
                <div className="inline-flex p-1 bg-white/80 rounded-full whitespace-nowrap border border-border/60 shadow-sm" data-testid="matches-tabs">
                    <TabButton active={tab === "direct"} onClick={() => setTab("direct")} testId="matches-tab-direct" count={matches.length}>
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                        <span className="sm:hidden">Swap</span><span className="hidden sm:inline">1-on-1 swap</span>
                    </TabButton>
                    <TabButton active={tab === "chain"} onClick={() => setTab("chain")} testId="matches-tab-chain" count={chains.length}>
                        <Link2 className="w-3.5 h-3.5" />
                        <span className="sm:hidden">Chain</span><span className="hidden sm:inline">Chain (3-way)</span>
                    </TabButton>
                    <TabButton active={tab === "buddies"} onClick={() => setTab("buddies")} testId="matches-tab-buddies" count={buddies.length}>
                        <Users className="w-3.5 h-3.5" />
                        <span className="sm:hidden">Buddy</span><span className="hidden sm:inline">Exchange Buddies</span>
                    </TabButton>
                </div>
            </motion.div>

            {tab === "direct" && <DirectMatches loading={loading} matches={matches} />}
            {tab === "chain" && <ChainMatches loading={loading} chains={chains} />}
            {tab === "buddies" && <BuddyMatches loading={loading} buddies={buddies} />}

            {/* ── Explainer ─────────────────────────────────────────── */}
            <motion.section {...reveal(0.15)} className="mt-14 pt-10 border-t border-border/60" data-testid="matches-explainer">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">How matching works</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2">Two ways to find your swap</h2>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                    Both types use the same rules: your destination must match someone's home, and your dates must overlap. We just connect the dots differently.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-white border border-border/60 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <ArrowLeftRight className="w-5 h-5 text-primary" strokeWidth={2.4} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">1-on-1</div>
                                <div className="font-display font-semibold">Mirror match</div>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                            You go to their city. They come to yours. Same dates, fair rent split.
                        </p>
                    </div>
                    <div className="bg-white border border-border/60 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-secondary/15 flex items-center justify-center">
                                <Link2 className="w-5 h-5 text-secondary" strokeWidth={2.4} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-secondary">3-way</div>
                                <div className="font-display font-semibold">Chain swap</div>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                            No direct match? We connect three students in a circle: A → B → C → A.
                        </p>
                    </div>
                </div>
            </motion.section>
        </div>
        </PullToRefresh>
    );
}

function TabButton({ active, onClick, testId, count, children }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-3 sm:px-5 py-2 rounded-full text-sm transition-all inline-flex items-center gap-1.5 shrink-0 ${active ? "bg-primary text-primary-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground font-medium"}`}
        >
            {children}
            <span className={`ml-0.5 text-xs ${active ? "opacity-90" : "opacity-70"}`}>({count})</span>
        </button>
    );
}

function DirectMatches({ loading, matches }) {
    if (loading) {
        return <div className="mt-6 grid md:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="h-56 bg-muted rounded-2xl animate-pulse" />)}</div>;
    }
    if (matches.length === 0) {
        return (
            <EmptyState
                icon={Sparkles}
                title="No 1-on-1 matches yet"
                body="Make sure your listing includes the destination you're visiting — that's how we find your mirror."
                cta={<Link to="/listings/new"><Button className="mt-2 rounded-full bg-primary hover:bg-primary/90" data-testid="no-matches-cta">List a room</Button></Link>}
                testId="no-matches"
            />
        );
    }
    return (
        <div className="mt-6 grid gap-4">
            {matches.map((m, i) => (
                <motion.div
                    key={i}
                    {...reveal(i)}
                    data-testid={`match-${i}`}
                    className="bg-white border border-border/60 rounded-3xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                    <div className="p-4 sm:p-5 flex items-center justify-between gap-3 bg-gradient-to-r from-primary/8 to-primary/[0.02] border-b border-border/50">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                                <Sparkles className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Match</div>
                                <div className="font-display text-xl font-bold tabular-nums">{m.score}%</div>
                            </div>
                        </div>
                        <div className="text-right">
                            {m.you_pay_extra > 0 && (
                                <>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">You pay extra</div>
                                    <div className="font-display text-lg font-bold text-primary">{formatPrice(m.you_pay_extra, m.their_room?.currency || "EUR")}</div>
                                </>
                            )}
                            {m.they_pay_extra > 0 && (
                                <>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">They pay extra</div>
                                    <div className="font-display text-lg font-bold text-secondary">{formatPrice(m.they_pay_extra, m.my_room?.currency || "EUR")}</div>
                                </>
                            )}
                            {m.you_pay_extra === 0 && m.they_pay_extra === 0 && (
                                <>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Rent</div>
                                    <div className="font-display text-lg font-bold">Equal</div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border/50">
                        <MatchSide label="Your room" room={m.my_room} img={imgOf(m.my_room)} />
                        <MatchSide label={`${m.their_room.owner_name} offers`} room={m.their_room} img={imgOf(m.their_room)} />
                    </div>
                    <div className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 border-t border-border/50 bg-muted/30">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <ArrowLeftRight className="w-4 h-4 text-primary" />
                            <span className="truncate">{m.my_room.city} ↔ {m.their_room.city}</span>
                        </div>
                        <div className="flex gap-2">
                            <Link to={`/rooms/${m.their_room.id}`}>
                                <Button variant="outline" className="rounded-full h-9" data-testid={`match-view-${i}`}>View</Button>
                            </Link>
                            <Link to={`/messages/${m.their_room.owner_id}?room=${m.their_room.id}`}>
                                <Button className="rounded-full h-9 bg-primary hover:bg-primary/90" data-testid={`match-message-${i}`}>
                                    <MessageCircle className="w-4 h-4 mr-1" /> Message
                                </Button>
                            </Link>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}

function ChainMatches({ loading, chains }) {
    if (loading) return <div className="mt-6 grid gap-4">{[1, 2].map((i) => <div key={i} className="h-72 bg-muted rounded-2xl animate-pulse" />)}</div>;
    if (chains.length === 0) {
        return (
            <EmptyState
                icon={Link2}
                title="No chain swaps yet"
                body="A chain connects three students in a circle (A → B → C → A). Rare but powerful. As the community grows, more emerge."
                testId="no-chain-matches"
            />
        );
    }
    return (
        <div className="mt-6 grid gap-4">
            {chains.map((c, i) => (
                <motion.div key={i} {...reveal(i)} data-testid={`chain-${i}`} className="bg-white border border-border/60 rounded-3xl overflow-hidden">
                    <div className="p-4 sm:p-5 bg-gradient-to-r from-secondary/10 to-secondary/[0.02] border-b border-border/50">
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-secondary/15 flex items-center justify-center shrink-0">
                                <Link2 className="w-5 h-5 text-secondary" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-secondary">3-way chain</div>
                                <div className="font-display text-base sm:text-lg font-bold truncate">
                                    {c.my_room.city} → {c.room_b.city} → {c.room_c.city} → {c.my_room.city}
                                </div>
                            </div>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> Overlap: {c.overlap_from} → {c.overlap_to}
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border/50">
                        <ChainNode label="Your room" badge="You stay home" room={c.my_room} img={imgOf(c.my_room)} />
                        <ChainNode label="You'll stay in" badge={`${c.room_b.owner_name} hosts`} room={c.room_b} img={imgOf(c.room_b)} highlight />
                        <ChainNode label="They'll stay in" badge={`${c.room_c.owner_name} hosts`} room={c.room_c} img={imgOf(c.room_c)} />
                    </div>
                    <div className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 border-t border-border/50 bg-muted/30">
                        <div className="text-sm min-w-0">
                            {c.same_currency && c.my_net_difference !== null ? (
                                c.my_net_difference > 0 ? <span>You'd pay <span className="font-semibold text-primary">{formatPrice(c.my_net_difference, c.my_room.currency || "EUR")}</span> more.</span>
                                    : c.my_net_difference < 0 ? <span>You'd receive <span className="font-semibold text-secondary">{formatPrice(Math.abs(c.my_net_difference), c.my_room.currency || "EUR")}</span> more.</span>
                                        : <span>Rent levels are equal across the chain.</span>
                            ) : <span className="text-muted-foreground">Different currencies — compare rents directly.</span>}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <Link to={`/messages/${c.room_b.owner_id}?room=${c.room_b.id}`}>
                                <Button variant="outline" size="sm" className="rounded-full" data-testid={`chain-message-b-${i}`}>
                                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> {c.room_b.owner_name.split(" ")[0]}
                                </Button>
                            </Link>
                            <Link to={`/messages/${c.room_c.owner_id}?room=${c.room_c.id}`}>
                                <Button variant="outline" size="sm" className="rounded-full" data-testid={`chain-message-c-${i}`}>
                                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> {c.room_c.owner_name.split(" ")[0]}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
}

function BuddyMatches({ loading, buddies }) {
    if (loading) return <div className="mt-6 grid md:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="h-40 bg-muted rounded-2xl animate-pulse" />)}</div>;
    return (
        <>
            <motion.div {...reveal(0)} className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-5" data-testid="buddy-explainer">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                        <HandCoins className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display text-lg font-semibold">What is an Exchange Buddy?</div>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                            Your buddy is heading to <em>your</em> home city while you're heading to <em>theirs</em>. Share housing tips, save on agency fees, help each other land softly. No room swap needed.
                        </p>
                    </div>
                </div>
            </motion.div>
            {buddies.length === 0 ? (
                <EmptyState
                    icon={Users}
                    title="No Exchange Buddies yet"
                    body="We match you with someone whose exchange destination = your home city AND home city = your destination. Set both on your profile so we can find them."
                    testId="no-buddy-matches"
                />
            ) : (
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                    {buddies.map((b, i) => (
                        <motion.div key={b.user.id} {...reveal(i)} data-testid={`buddy-${i}`} className="bg-white border border-border/60 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-display text-lg font-semibold truncate">{b.user.name}</div>
                                    {b.their_university && <div className="text-xs text-muted-foreground truncate">{b.their_university}</div>}
                                    {b.their_home_city && (
                                        <div className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1">
                                            <MapPin className="w-3.5 h-3.5" /> Based in {b.their_home_city}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 rounded-full px-3 py-1 shrink-0">Buddy</div>
                            </div>
                            <div className="mt-3 text-xs text-muted-foreground space-y-1">
                                <div>You go to <span className="font-medium text-foreground capitalize">{(b.you_go_to || []).join(", ")}</span></div>
                                <div>They go to <span className="font-medium text-foreground capitalize">{(b.they_go_to || []).join(", ")}</span></div>
                            </div>
                            <Link to={`/messages/${b.user.id}`}>
                                <Button className="mt-4 w-full rounded-full bg-primary hover:bg-primary/90" data-testid={`buddy-message-${i}`}>
                                    <MessageCircle className="w-4 h-4 mr-1" /> Send message
                                </Button>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            )}
        </>
    );
}

function MatchSide({ label, room, img }) {
    return (
        <div className="p-4 sm:p-5 flex gap-3 min-w-0">
            <img loading="lazy" decoding="async" src={img} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground truncate">{label}</div>
                <div className="font-display text-base sm:text-lg font-semibold truncate mt-1">{room.title}</div>
                <div className="text-xs sm:text-sm text-muted-foreground truncate">{room.city}, {room.country}</div>
                <div className="text-xs sm:text-sm mt-1 font-semibold">{formatPrice(room.rent_per_month, room.currency || "EUR", { perMonth: true })}</div>
            </div>
        </div>
    );
}

function ChainNode({ label, badge, room, img, highlight }) {
    return (
        <div className={`p-4 sm:p-5 ${highlight ? "bg-secondary/5" : ""}`}>
            <div className="flex gap-3">
                <img loading="lazy" decoding="async" src={img} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover shrink-0" />
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
                    <div className="font-display text-sm sm:text-base font-semibold truncate mt-0.5">{room.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{room.city}, {room.country}</div>
                    <div className="text-xs mt-1 font-semibold">{formatPrice(room.rent_per_month, room.currency || "EUR", { perMonth: true })}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 truncate">{badge}</div>
                </div>
            </div>
        </div>
    );
}

function EmptyState({ icon: Icon, title, body, cta, testId }) {
    return (
        <motion.div {...reveal(0)} className="mt-6 border border-dashed border-border rounded-3xl p-10 text-center bg-white/40" data-testid={testId}>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-7 h-7 text-primary" />
            </div>
            <h3 className="font-display text-lg font-semibold mt-4">{title}</h3>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto text-sm">{body}</p>
            {cta}
        </motion.div>
    );
}
